import React, { useMemo, useRef, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { submitCompanyApplication } from "../api/companyApplications";
import { checkNumistrailIdAvailable } from "../api/users";
import {
  normalizeInput,
  validateCompanyAddress,
  validateCompanyCountryOptional,
  validateCompanyName,
  validateCompanyWebsite,
  validateEmail,
  validateNumistrailId,
  validatePassword,
  validatePhone,
} from "../utils/validation";

const LEGAL_VERSION_TOS = "tos_v5_2026_04";
const LEGAL_VERSION_PRIVACY = "privacy_v4_2026_03";
const LEGAL_VERSION_DISCLAIMER = "disclaimer_v3_2026_03";
const CATEGORY_OPTIONS = [
  "CERTIFICATION_ENTITY",
  "STORE",
  "DEALER",
  "AUCTION_HOUSE",
];
const PUBLIC_WEBSITE_URL = (process.env.REACT_APP_WEB_URL || "https://numistrail.com").trim();

export function PartnerApply() {
  const { t, i18n } = useTranslation();
  const [form, setForm] = useState({
    numistrail_id: "",
    email: "",
    password: "",
    business_category: "CERTIFICATION_ENTITY",
    legal_name: "",
    display_name: "",
    website: "",
    phone: "",
    address: "",
    country: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [touched, setTouched] = useState({});
  const [suggestion, setSuggestion] = useState(null);
  const [legalAccepted, setLegalAccepted] = useState(false);
  const [legalTouched, setLegalTouched] = useState(false);
  const idInputRef = useRef(null);

  const handleChange = (key) => (event) => {
    const rawValue = event.target.value;
    const value = key === "email" ? rawValue.toLowerCase() : rawValue;
    setForm((current) => ({ ...current, [key]: value }));
    if (key === "numistrail_id") {
      setSuggestion(null);
    }
  };

  const markTouched = (key) => () =>
    setTouched((prev) => ({ ...prev, [key]: true }));

  const validationErrors = useMemo(() => ({
    numistrail_id: validateNumistrailId(form.numistrail_id),
    email: validateEmail(form.email),
    password: validatePassword(form.password),
    legal_name: validateCompanyName(form.legal_name, "legalName"),
    display_name: validateCompanyName(form.display_name, "displayName"),
    website: validateCompanyWebsite(form.website),
    phone: validatePhone(form.phone),
    address: validateCompanyAddress(form.address, { required: true }),
    country: validateCompanyCountryOptional(form.country),
  }), [form]);

  const isFormValid = useMemo(
    () => Object.values(validationErrors).every((message) => !message),
    [validationErrors]
  );

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!isFormValid) {
      setTouched({
        numistrail_id: true,
        email: true,
        password: true,
        legal_name: true,
        display_name: true,
        website: true,
        phone: true,
        address: true,
        country: true,
      });
      setError(t("validation.fixErrors"));
      return;
    }
    if (!legalAccepted) {
      setLegalTouched(true);
      setError(t("validation.legalAcceptance.required"));
      return;
    }

    if (showPassword) {
      setShowPassword(false);
      await new Promise((resolve) => setTimeout(resolve, 0));
    }

    try {
      await submitCompanyApplication({
        numistrail_id: normalizeInput(form.numistrail_id, { lowercase: true }),
        email: normalizeInput(form.email, { lowercase: true }),
        password: normalizeInput(form.password),
        legal_acceptance: true,
        legal_version_tos: LEGAL_VERSION_TOS,
        legal_version_privacy: LEGAL_VERSION_PRIVACY,
        legal_version_disclaimer: LEGAL_VERSION_DISCLAIMER,
        business_category: form.business_category,
        legal_name: normalizeInput(form.legal_name),
        display_name: normalizeInput(form.display_name),
        website: normalizeInput(form.website),
        phone: normalizeInput(form.phone) || undefined,
        address: normalizeInput(form.address),
        country: normalizeInput(form.country),
        default_locale: i18n.language,
      });

      setError(null);
      setSuccess(true);
    } catch (err) {
      setSuggestion(null);
      setSuccess(false);
      const messageKey = err.response?.data?.message_key;
      const message = err.response?.data?.message;
      const resolvedMessage = t(messageKey || "errors.partnerApplyFailed", {
        defaultValue: message || t("errors.partnerApplyFailed"),
      });
      setError(resolvedMessage);
      if (messageKey === "errors.users.numis_duplicate" || (message || "").toLowerCase().includes("numistrail")) {
        const suggested = await suggestUsername(form.numistrail_id);
        if (suggested) {
          setSuggestion(suggested);
        }
      }
    }
  };

  const applySuggestion = () => {
    if (!suggestion) {
      return;
    }
    setForm((current) => ({ ...current, numistrail_id: suggestion }));
    setSuggestion(null);
    setError(null);
    setTimeout(() => idInputRef.current?.focus(), 0);
  };

  const handleLegalChange = (event) => {
    const checked = event.target.checked;
    setLegalAccepted(checked);
    setLegalTouched(true);
    if (checked && error === t("validation.legalAcceptance.required")) {
      setError(null);
    }
  };

  const isDisabled = success;

  return (
    <div className="register-screen">
      <div className="register-card register-card--wide">
        <div className="register-card__header">
          <h2 className="register-title">{t("partnerApply.title")}</h2>
          <p className="register-subtitle">{t("partnerApply.subtitle")}</p>
        </div>

        <div className="register-status register-status--info partner-apply__note">
          {t("partnerApply.flowNote")}
        </div>

        {error && (
          <div className="register-status register-status--error partner-apply__status">
            <p>{error}</p>
            <a className="partner-apply__secondary-link" href={PUBLIC_WEBSITE_URL}>
              {t("partnerApply.backToWebsite")}
            </a>
          </div>
        )}

        {success && (
          <div className="register-status register-status--success partner-apply__status">
            <strong>{t("partnerApply.successTitle")}</strong>
            <p>{t("partnerApply.successBody")}</p>
            <a className="partner-apply__success-link" href="/login">
              {t("register.loginLink")}
            </a>
            <a className="partner-apply__secondary-link" href={PUBLIC_WEBSITE_URL}>
              {t("partnerApply.backToWebsite")}
            </a>
          </div>
        )}

        {suggestion && (
          <div className="register-status register-status--info">
            <span>{t("register.suggestedId")}</span>
            <button type="button" className="register-suggestion" onClick={applySuggestion}>
              {suggestion}
            </button>
          </div>
        )}

        <form className="register-form" onSubmit={handleSubmit} method="post" autoComplete="on">
          <div className="register-field">
            <label className="register-label" htmlFor="partner-numis-id">
              {t("partnerApply.idLabel")}
            </label>
            <input
              className="register-input"
              id="partner-numis-id"
              name="username"
              type="text"
              autoComplete="username"
              placeholder={t("partnerApply.idPlaceholder")}
              ref={idInputRef}
              value={form.numistrail_id}
              onChange={handleChange("numistrail_id")}
              onBlur={markTouched("numistrail_id")}
              disabled={isDisabled}
            />
            <p className="register-field__hint">{t("partnerApply.idHint")}</p>
            {touched.numistrail_id && validationErrors.numistrail_id ? (
              <p className="register-field__error" role="alert">{t(validationErrors.numistrail_id)}</p>
            ) : null}
          </div>

          <div className="register-field">
            <label className="register-label" htmlFor="partner-email">
              {t("partnerApply.emailLabel")}
            </label>
            <input
              className="register-input"
              id="partner-email"
              name="email"
              type="email"
              autoComplete="email"
              placeholder={t("partnerApply.emailPlaceholder")}
              value={form.email}
              onChange={handleChange("email")}
              onBlur={markTouched("email")}
              disabled={isDisabled}
            />
            <p className="register-field__hint">{t("partnerApply.emailHint")}</p>
            {touched.email && validationErrors.email ? (
              <p className="register-field__error" role="alert">{t(validationErrors.email)}</p>
            ) : null}
          </div>

          <div className="register-field">
            <label className="register-label" htmlFor="partner-password">
              {t("partnerApply.passwordLabel")}
            </label>
            <div className="login-password-input">
              <input
                className="register-input"
                id="partner-password"
                name="password"
                type={showPassword ? "text" : "password"}
                placeholder={t("partnerApply.passwordPlaceholder")}
                value={form.password}
                onChange={handleChange("password")}
                onBlur={markTouched("password")}
                autoComplete="new-password"
                disabled={isDisabled}
              />
              <button
                type="button"
                className="login-toggle"
                onClick={() => setShowPassword((prev) => !prev)}
                aria-label={showPassword ? t("login.hidePassword") : t("login.showPassword")}
                disabled={isDisabled}
              >
                {showPassword ? "\u{1F648}" : "\u{1F441}\uFE0F"}
              </button>
            </div>
            {touched.password && validationErrors.password ? (
              <p className="register-field__error" role="alert">{t(validationErrors.password)}</p>
            ) : null}
          </div>

          <div className="register-field">
            <label className="register-label" htmlFor="partner-category">
              {t("partnerApply.businessCategoryLabel")}
            </label>
            <select
              className="register-input cert-input--select"
              id="partner-category"
              value={form.business_category}
              onChange={handleChange("business_category")}
              disabled={isDisabled}
            >
              {CATEGORY_OPTIONS.map((option) => (
                <option key={option} value={option}>
                  {t(`partnerApply.businessCategory.options.${option}`)}
                </option>
              ))}
            </select>
            <p className="register-field__hint">{t("partnerApply.businessCategoryHint")}</p>
          </div>

          <div className="register-field">
            <label className="register-label" htmlFor="partner-legal-name">
              {t("partnerApply.legalNameLabel")}
            </label>
            <input
              className="register-input"
              id="partner-legal-name"
              placeholder={t("partnerApply.legalNamePlaceholder")}
              value={form.legal_name}
              onChange={handleChange("legal_name")}
              onBlur={markTouched("legal_name")}
              disabled={isDisabled}
            />
            <p className="register-field__hint">{t("partnerApply.legalNameHint")}</p>
            {touched.legal_name && validationErrors.legal_name ? (
              <p className="register-field__error" role="alert">{t(validationErrors.legal_name)}</p>
            ) : null}
          </div>

          <div className="register-field">
            <label className="register-label" htmlFor="partner-display-name">
              {t("partnerApply.displayNameLabel")}
            </label>
            <input
              className="register-input"
              id="partner-display-name"
              placeholder={t("partnerApply.displayNamePlaceholder")}
              value={form.display_name}
              onChange={handleChange("display_name")}
              onBlur={markTouched("display_name")}
              disabled={isDisabled}
            />
            <p className="register-field__hint">{t("partnerApply.displayNameHint")}</p>
            {touched.display_name && validationErrors.display_name ? (
              <p className="register-field__error" role="alert">{t(validationErrors.display_name)}</p>
            ) : null}
          </div>

          <div className="register-field">
            <label className="register-label" htmlFor="partner-website">
              {t("partnerApply.websiteLabel")}
            </label>
            <input
              className="register-input"
              id="partner-website"
              placeholder={t("partnerApply.websitePlaceholder")}
              value={form.website}
              onChange={handleChange("website")}
              onBlur={markTouched("website")}
              disabled={isDisabled}
            />
            {touched.website && validationErrors.website ? (
              <p className="register-field__error" role="alert">{t(validationErrors.website)}</p>
            ) : null}
          </div>

          <div className="register-field">
            <label className="register-label" htmlFor="partner-phone">
              {t("partnerApply.phoneLabel")}
            </label>
            <input
              className="register-input"
              id="partner-phone"
              placeholder={t("partnerApply.phonePlaceholder")}
              value={form.phone}
              onChange={handleChange("phone")}
              onBlur={markTouched("phone")}
              disabled={isDisabled}
            />
            {touched.phone && validationErrors.phone ? (
              <p className="register-field__error" role="alert">{t(validationErrors.phone)}</p>
            ) : null}
          </div>

          <div className="register-field">
            <label className="register-label" htmlFor="partner-country">
              {t("partnerApply.countryLabel")}
            </label>
            <input
              className="register-input"
              id="partner-country"
              placeholder={t("partnerApply.countryPlaceholder")}
              value={form.country}
              onChange={handleChange("country")}
              onBlur={markTouched("country")}
              disabled={isDisabled}
            />
            {touched.country && validationErrors.country ? (
              <p className="register-field__error" role="alert">{t(validationErrors.country)}</p>
            ) : null}
          </div>

          <div className="register-field">
            <label className="register-label" htmlFor="partner-address">
              {t("partnerApply.addressLabel")}
            </label>
            <input
              className="register-input"
              id="partner-address"
              type="text"
              placeholder={t("partnerApply.addressPlaceholder")}
              value={form.address}
              onChange={handleChange("address")}
              onBlur={markTouched("address")}
              disabled={isDisabled}
            />
            {touched.address && validationErrors.address ? (
              <p className="register-field__error" role="alert">{t(validationErrors.address)}</p>
            ) : null}
          </div>

          <div className="register-legal">
            <input
              id="partner-legal"
              type="checkbox"
              checked={legalAccepted}
              onChange={handleLegalChange}
              onBlur={() => setLegalTouched(true)}
              disabled={isDisabled}
            />
            <label className="register-legal__label" htmlFor="partner-legal">
              <Trans
                i18nKey="register.legalAcceptance"
                components={{
                  terms: <a href="/terms" target="_blank" rel="noopener noreferrer" />,
                  privacy: <a href="/privacy" target="_blank" rel="noopener noreferrer" />,
                  disclaimer: <a href="/disclaimer" target="_blank" rel="noopener noreferrer" />,
                }}
              />
            </label>
          </div>
          {legalTouched && !legalAccepted ? (
            <p className="register-field__error" role="alert">
              {t("validation.legalAcceptance.required")}
            </p>
          ) : null}

          <button
            className="register-submit"
            type="submit"
            disabled={!isFormValid || !legalAccepted || isDisabled}
          >
            {t("partnerApply.submit")}
          </button>
        </form>

        <div className="legal-footer">
          <p className="legal-note">{t("legal.brandNotice")}</p>
          <div className="legal-links">
            <a href="/terms" target="_blank" rel="noopener noreferrer">
              {t("legal.terms")}
            </a>
            <span className="legal-links__sep" aria-hidden="true">·</span>
            <a href="/privacy" target="_blank" rel="noopener noreferrer">
              {t("legal.privacy")}
            </a>
            <span className="legal-links__sep" aria-hidden="true">·</span>
            <a href="/disclaimer" target="_blank" rel="noopener noreferrer">
              {t("legal.disclaimer")}
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}

async function suggestUsername(base) {
  const normalizedBase = normalizeInput(base || "partner", { lowercase: true })
    .replace(/[^a-z0-9_]/g, "")
    .slice(0, 40) || "partner";
  let attempt = 0;
  while (attempt < 5) {
    const candidate = `${normalizedBase}${Math.floor(Math.random() * 1000)}`;
    try {
      const response = await checkNumistrailIdAvailable(candidate);
      if (response.data?.available) {
        return candidate;
      }
    } catch {
      // Ignore availability lookup failures and try another candidate.
    }
    attempt += 1;
  }
  return null;
}

